import { createSelector } from '@reduxjs/toolkit';

import { objectSelector as _get } from '../../utils';

const checkoutSelector = state => state.checkout;

export const selectIsReady = createSelector(
  checkoutSelector,
  checkout => checkout.isReady
);

export const selectTab = createSelector(
  checkoutSelector,
  checkout => checkout.currentTab
);

export const selectErrors = createSelector(
  checkoutSelector,
  checkout => checkout.errors
);

export const selectPending = createSelector(
  checkoutSelector,
  checkout => checkout.pending
);

export const selectDeliveryOptions = createSelector(
  checkoutSelector,
  checkout => checkout.deliveryOptions
);

export const selectCurrentDeliveryOptionId = createSelector(
  checkoutSelector,
  checkout => checkout.deliveryOptionId
);

export const selectAddresses = createSelector(
  checkoutSelector,
  checkout => checkout.addresses
);

export const selectCurrentAddressId = createSelector(
  checkoutSelector,
  checkout => checkout.addressId
);

export const selectCurrentAddress = createSelector(
  [selectAddresses, selectCurrentAddressId],
  (addresses, currentId) => addresses.find(({pk}) => pk === currentId)
);

export const selectCurrentAddressCity = createSelector(
  selectCurrentAddress,
  address => _get(address, 'city.name', '')
);

export const selectCurrentBillingId = createSelector(
  checkoutSelector,
  checkout => checkout.billingId
);

export const selectCurrentBillingAddress = createSelector(
  [selectAddresses, selectCurrentBillingId],
  (addresses, currentId) => addresses.find(({ pk }) => pk === currentId)
);

export const selectShippingOptions = createSelector(
  [checkoutSelector, selectAddresses],
  (checkout, addresses) => addresses.length ? checkout.shippingOptions : []
);

export const selectCurrentShippingId = createSelector(
  checkoutSelector,
  checkout => checkout.shippingId
);

export const selectFrameUrl = createSelector(
  checkoutSelector,
  checkout => checkout.contextExtras.FrameUrl
);

export const selectHepsipayAvailability = createSelector(
  checkoutSelector,
  checkout => checkout.hepsipayAvailability
);

export const selectCurrentShipping = createSelector(
  [selectShippingOptions, selectCurrentShippingId],
  (shippingOptions, currentId) => shippingOptions.find(({ pk }) => pk === currentId)
);

export const selectShippingAmount = createSelector(
  checkoutSelector,
  checkout => checkout.shippingAmount
);

export const selectTotalAmount = createSelector(
  checkoutSelector,
  checkout => checkout.totalAmount
);

export const selectTotalAmountWithInterest = createSelector(
  checkoutSelector,
  checkout => checkout.totalAmountWithInterest
);

export const selectPreOrder = createSelector(
  checkoutSelector,
  checkout => checkout.preOrder
);

export const selectPreOrderBasket = createSelector(
  checkoutSelector,
  checkout => checkout.basket
);

export const selectSummaryData = createSelector(
  [selectShippingAmount, selectTotalAmount, selectTotalAmountWithInterest, selectPreOrderBasket],
  (shippingAmount, totalAmount, totalAmountWithInterest, basket) => {
    return {
      shippingAmount,
      totalAmount,
      totalAmountWithInterest,
      basket
    };
  }
);

export const selectAddressAndShippingSet = createSelector(
  [selectCurrentAddress, selectCurrentShipping],
  (address, shipping) => !!address && !!shipping
);

export const selectAddressAndShipping = createSelector(
  [selectCurrentAddress, selectCurrentShipping],
  (address, shipping) => {
    return { address, shipping };
  }
);

export const selectSameBillAddress = createSelector(
  checkoutSelector,
  checkout => checkout.sameBillAddress
);

export const selectCurrency = createSelector(
  checkoutSelector,
  checkout => checkout.currency
);

export const selectPaymentOptions = createSelector(
  checkoutSelector,
  checkout => checkout.paymentOptions
);

export const selectCurrentPaymentOptionId = createSelector(
  checkoutSelector,
  checkout => checkout.paymentOptionId
);

export const selectCurrentPaymentOption = createSelector(
  [selectPaymentOptions, selectCurrentPaymentOptionId],
  (paymentOptions, currentId) => paymentOptions.find(({ pk }) => pk === currentId)
);

export const selectUnavailablePaymentOptions = createSelector(
  checkoutSelector,
  checkout => checkout.unavailablePaymentOptions
);

export const selectCardHolder = createSelector(
  checkoutSelector,
  checkout => checkout.cardHolder
);

export const selectCardNumber = createSelector(
  checkoutSelector,
  checkout => checkout.cardNumber
);

export const selectCardMonth = createSelector(
  checkoutSelector,
  checkout => checkout.cardMonth
);

export const selectCardYear = createSelector(
  checkoutSelector,
  checkout => checkout.cardYear
);

export const selectCardCVV = createSelector(
  checkoutSelector,
  checkout => checkout.cardCVV
);

export const selectUseThreeD = createSelector(
  checkoutSelector,
  checkout => checkout.useThreeD
);

export const selectBinNumberLength = createSelector(
  checkoutSelector,
  checkout => checkout.binNumberLength
);

export const selectBinNumber = createSelector(
  [selectCardNumber, selectBinNumberLength],
  (cardNumber, binNumberLength) => (cardNumber && cardNumber.length >= binNumberLength) ?
    cardNumber.substring(0, binNumberLength) : null
);

export const selectCardType = createSelector(
  checkoutSelector,
  checkout => checkout.cardType
);

export const selectCardTypeName = createSelector(
  selectCardType,
  cardType => _get(cardType, 'name', '')
);

export const selectCardTypeImage = createSelector(
  selectCardType,
  cardType => _get(cardType, 'logo', '')
);

export const selectInstallments = createSelector(
  checkoutSelector,
  checkout => checkout.installments
);

export const selectCurrentInstallmentId = createSelector(
  checkoutSelector,
  checkout => checkout.installmentId
);

export const selectAgreement = createSelector(
  checkoutSelector,
  checkout => checkout.agreement
);

export const selectFundsTransferAccounts = createSelector(
  checkoutSelector,
  checkout => checkout.fundsTransferAccounts
);

export const selectCurrentFundsTransferAccountId = createSelector(
  checkoutSelector,
  checkout => checkout.fundsTransferAccountId
);

export const selectCurrentFundsTransferAccount = createSelector(
  [selectFundsTransferAccounts, selectCurrentFundsTransferAccountId],
  (transferAccounts, currentId) => transferAccounts.find(({ pk }) => pk === currentId)
);


export const selectCreditPaymentOptions = createSelector(
  checkoutSelector,
  checkout => checkout.creditPaymentOptions
);

export const selectCurrentCreditPaymentOptionId = createSelector(
  checkoutSelector,
  checkout => checkout.creditPaymentOptionId
);

export const selectCurrentCreditPaymentOption = createSelector(
  [selectCreditPaymentOptions, selectCurrentCreditPaymentOptionId],
  (creditPaymentOptions, currentId) => creditPaymentOptions.find(({ pk }) => pk === currentId)
);
export const selectPayOnDeliveryMethods = createSelector(
  checkoutSelector,
  checkout => checkout.payOnDeliveryMethods
);

export const selectCurrentPayOnDeliveryMethod = createSelector(
  checkoutSelector,
  checkout => checkout.payOnDeliveryMethod
);

export const selectSampleProducts = createSelector(
  checkoutSelector,
  checkout => checkout.sampleProducts
);

export const selectSelectedSampleProducts = createSelector(
  checkoutSelector,
  checkout => checkout.selectedSampleProducts
);

export const selectSampleProductMaxAllowed = createSelector(
  checkoutSelector,
  checkout => checkout.sampleProductMaxAllowed
);

export const selectSampleProductsSaved = createSelector(
  checkoutSelector,
  checkout => checkout.sampleProductsSaved
);

export const selectLoyaltyCardNumber = createSelector(
  checkoutSelector,
  checkout => checkout.loyaltyCardNumber
);

export const selectLoyaltyTotalAmount = createSelector(
  checkoutSelector,
  checkout => checkout.loyaltyTotalAmount
);

export const selectLoyaltyAmount = createSelector(
  checkoutSelector,
  checkout => checkout.loyaltyAmount
);

export const selectLoyaltyAppliedAmount = createSelector(
  checkoutSelector,
  checkout => checkout.loyaltyAppliedAmount
);

export const selectLoyaltyData = createSelector(
  [selectLoyaltyCardNumber, selectLoyaltyTotalAmount, selectLoyaltyAmount, selectLoyaltyAppliedAmount],
  (loyaltyCardNumber, loyaltyTotalAmount, loyaltyAmount, loyaltyAppliedAmount) => {
    return {
      loyaltyCardNumber,
      loyaltyTotalAmount,
      loyaltyAmount,
      loyaltyAppliedAmount
    };
  }
);

export const selectRetailStores = createSelector(
  checkoutSelector,
  checkout => checkout.retailStores
);

export const selectCurrentRetailStore = createSelector(
  checkoutSelector,
  checkout => checkout.selectedRetailStore
);

export const selectGiftable = createSelector(
  checkoutSelector,
  checkout => checkout.giftable
);

export const selectGiftNote = createSelector(
  checkoutSelector,
  checkout => checkout.giftNote
);

export const selectMasterPassOrder = createSelector(
  checkoutSelector,
  checkout => checkout.masterPassOrder
);

export const selectHasRewards = createSelector(
  checkoutSelector,
  checkout => checkout.hasRewards,
);
